import React from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    Badge,
    Button
} from '@mui/material';
import {
    Close as CloseIcon,
    Notifications as NotificationsIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    CheckCircle as SuccessIcon,
    DeleteSweep
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useThemeContext } from '../contexts/ThemeContext';
import { useNotification } from '../contexts/NotificationContext';

interface NotificationDrawerProps {
    open: boolean;
    onClose: () => void;
}

const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
    open,
    onClose
}) => {
    const { t } = useLanguage();
    const { mode } = useThemeContext();
    const { userNotifications, markAsRead, deleteUserNotification, fetchUserNotifications } = useNotification();

    const handleMarkAllRead = async () => {
        // Temporary implementation: loop through unread
        const unread = userNotifications.filter(n => !n.NOT_IS_READ);
        for (const n of unread) {
            await markAsRead(n.NOT_UIDF);
        }
    };

    const handleClearAll = async () => {
        // Temporary implementation: loop through all
        for (const n of userNotifications) {
            await deleteUserNotification(n.NOT_UIDF);
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'WARNING': return <WarningIcon color="warning" />;
            case 'ERROR': return <ErrorIcon color="error" />;
            case 'SUCCESS': return <SuccessIcon color="success" />;
            default: return <InfoIcon color="info" />;
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: 350, maxWidth: '100%' }
            }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                        {t('common.actions.notifications') || "Notifications"}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', gap: 1, bgcolor: 'action.hover' }}>
                <Button size="small" onClick={handleMarkAllRead}>
                    {t('common.actions.markAllRead') || "Mark all read"}
                </Button>
                <Button size="small" color="error" onClick={handleClearAll} startIcon={<DeleteSweep />}>
                    {t('common.actions.clear') || "Clear"}
                </Button>
            </Box>

            <List sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
                {userNotifications.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">
                            {t('common.messages.noNotifications') || "No new notifications"}
                        </Typography>
                    </Box>
                ) : (
                    userNotifications.map((notif) => (
                        <React.Fragment key={notif.NOT_UIDF}>
                            <ListItem
                                alignItems="flex-start"
                                sx={{ bgcolor: notif.NOT_IS_READ ? 'transparent' : 'action.selected', cursor: 'pointer' }}
                                onClick={() => markAsRead(notif.NOT_UIDF)}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: 'transparent' }}>
                                        {getIcon(notif.NOT_TYPE)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle2" fontWeight={notif.NOT_IS_READ ? 'normal' : 'bold'}>
                                            {notif.NOT_TITLE}
                                        </Typography>
                                    }
                                    secondary={
                                        <>
                                            <Typography variant="body2" color="text.primary" component="span" sx={{ display: 'block', mb: 0.5 }}>
                                                {notif.NOT_MESSAGE}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(notif.NOT_DATE).toLocaleString()}
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))
                )}
            </List>
        </Drawer>
    );
};

export default NotificationDrawer;
